import { useMutation, useQueryClient } from '@tanstack/react-query'
import ActivityLog from '../../model/ActivityLog'
import Badge from '../../model/Badge'
import { dataSource } from '../../service/domain/_service.config'
import { logError } from '../../util/errorLogger'
import { useChallengeId } from '../challenge/useChallengeId'
import { useAllBadges } from './useAllBadges'
import { USER_BADGES_QUERY_KEY } from './useUserBadges'

export function useCheckBadges() {
  const queryClient = useQueryClient()
  const { data: allBadges } = useAllBadges()
  const challengeID = useChallengeId()

  return useMutation({
    mutationFn: async ({
      userID,
      activityLog,
      activityLogs,
    }: {
      userID: string
      activityLog: ActivityLog
      activityLogs: ActivityLog[]
    }): Promise<Badge[]> => {
      if (!allBadges) return []

      const [userBadgesData, activities] = await Promise.all([
        dataSource.getUserBadges(userID, challengeID),
        dataSource.getActivities(challengeID),
      ])
      const activityMap = new Map(activities.map((a) => [a.id, a]))
      const earnedBadgeIDs = new Set(userBadgesData.map((eb) => eb.badge.id))

      const newlyEarnedBadges: Badge[] = []
      const earnedAt = new Date()

      const calculateValues = (badge: Badge, activityLogs: ActivityLog[], currentLog: ActivityLog) => {
        const relevantLogs = activityLogs.filter((log) =>
          badge.appliesToActivity(log.activityID, activityMap.get(log.activityID)),
        )

        switch (badge.requirementType) {
          case 'count':
            return {
              current: 1,
              cumulative: relevantLogs.length,
            }
          case 'time':
          case 'distance':
            return {
              current: currentLog.amount,
              cumulative: relevantLogs.reduce((sum, log) => sum + log.amount, 0),
            }
          case 'streak':
            return { current: 0, cumulative: 0 }
          default:
            return { current: 0, cumulative: 0 }
        }
      }

      const userBadges = allBadges.filter((b) => b.scope === 'user')
      for (const badge of userBadges) {
        if (earnedBadgeIDs.has(badge.id) || !badge.isActive) continue

        const { current, cumulative } = calculateValues(badge, activityLogs, activityLog)
        const activity = activityMap.get(activityLog.activityID)
        if (badge.checkRequirementsMet(current, cumulative, activityLog.activityID, activity)) {
          await dataSource.saveUserBadge(userID, badge.id, earnedAt)
          newlyEarnedBadges.push(badge)
        }
      }

      // Invalidate user badges cache
      queryClient.invalidateQueries({ queryKey: USER_BADGES_QUERY_KEY(userID, challengeID) })

      return newlyEarnedBadges
    },
    onError: (error) => {
      void logError(error, { hook: 'useCheckBadges' })
    },
  })
}
